import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import or.app.orientalrecepies.network.models.Category
import or.app.orientalrecepies.network.models.Ingredient
import or.app.orientalrecepies.network.models.Instruction
import or.app.orientalrecepies.network.models.Recipe
import or.app.orientalrecepies.network.models.ResultState
import or.app.orientalrecepies.network.repository.RecipeService
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Header
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Main
import org.jetbrains.compose.web.dom.Nav
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr
import org.jetbrains.compose.web.dom.Ul
import org.w3c.files.FileReader
import org.w3c.files.get

sealed class Screen {
    data object Dashboard : Screen()
    data object PendingRecipes : Screen()
    data object Categories : Screen()
    data class Details(val recipe: Recipe) : Screen()
    data object AddRecipe : Screen()
    data class EditRecipe(val recipe: Recipe) : Screen()
}

// Uklonjen fun main() odavde, on je sada u Main.kt

@Composable
fun AdminApp() {
    AdminDashboard()
}

@Composable
fun AdminCategoriesPage(
    state: ResultState<List<Category>>,
    onAddCategory: () -> Unit,
    onEditCategory: (Category) -> Unit,
    onDeleteCategory: (Category) -> Unit
) {
    Main(attrs = {
        classes(
            "flex-1",
            "overflow-y-auto",
            "no-scrollbar",
            "px-4",
            "py-8",
            "sm:p-8",
            "bg-nano-background/50"
        )
    }) {
        Div(attrs = { classes("max-w-7xl", "mx-auto") }) {
            Div(attrs = {
                classes(
                    "flex",
                    "flex-col",
                    "sm:flex-row",
                    "justify-between",
                    "items-start",
                    "sm:items-center",
                    "gap-4",
                    "mb-8"
                )
            }) {
                H2(attrs = { classes("text-2xl", "font-bold") }) { Text("Sve Kategorije") }
                Button(attrs = {
                    classes(
                        "w-full",
                        "sm:w-auto",
                        "bg-nano-primary",
                        "text-black",
                        "px-6",
                        "py-3",
                        "rounded-xl",
                        "font-bold",
                        "hover:scale-105",
                        "transition-transform"
                    )
                    onClick { onAddCategory() }
                }) {
                    Text("+ Dodaj Kategoriju")
                }
            }

            when (state) {
                is ResultState.Loading -> {
                    Div(attrs = {
                        classes(
                            "grid",
                            "grid-cols-1",
                            "sm:grid-cols-2",
                            "md:grid-cols-3",
                            "lg:grid-cols-4",
                            "gap-6"
                        )
                    }) {
                        repeat(8) {
                            SkeletonLoader(listOf("h-48", "rounded-3xl"))
                        }
                    }
                }

                is ResultState.Error -> {
                    Div(attrs = {
                        classes(
                            "bg-red-500/10",
                            "p-6",
                            "rounded-2xl",
                            "text-red-500"
                        )
                    }) {
                        Text(state.message ?: "Greška pri učitavanju kategorija")
                    }
                }

                is ResultState.Success -> {
                    Div(attrs = {
                        classes(
                            "grid",
                            "grid-cols-1",
                            "sm:grid-cols-2",
                            "md:grid-cols-3",
                            "lg:grid-cols-4",
                            "gap-6"
                        )
                    }) {
                        state.data.forEach { category ->
                            AdminCategoryCard(
                                category = category,
                                onEdit = { onEditCategory(category) },
                                onDelete = { onDeleteCategory(category) }
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun AdminCategoryCard(category: Category, onEdit: () -> Unit, onDelete: () -> Unit) {
    Div(attrs = {
        classes(
            "bg-nano-surface", "p-8", "rounded-3xl", "border", "border-white/5",
            "hover:border-nano-primary/30", "transition-all", "group", "cursor-pointer",
            "flex", "flex-col", "items-center", "gap-4", "text-center", "relative",
            "min-w-0" // Sprečava overflow kontejnera
        )
    }) {
        // Actions
        Div(attrs = {
            classes(
                "absolute",
                "top-4",
                "right-4",
                "flex",
                "gap-2",
                "opacity-0",
                "group-hover:opacity-100",
                "transition-opacity"
            )
        }) {
            Button(attrs = {
                classes(
                    "p-2",
                    "bg-blue-500/10",
                    "text-blue-500",
                    "hover:bg-blue-500/20",
                    "rounded-lg",
                    "transition-colors"
                )
                onClick {
                    it.stopImmediatePropagation()
                    onEdit()
                }
            }) {
                MaterialIcon("edit", "text-sm")
            }
            Button(attrs = {
                classes(
                    "p-2",
                    "bg-red-500/10",
                    "text-red-500",
                    "hover:bg-red-500/20",
                    "rounded-lg",
                    "transition-colors"
                )
                onClick {
                    it.stopImmediatePropagation()
                    onDelete()
                }
            }) {
                MaterialIcon("delete", "text-sm")
            }
        }

        Div(attrs = {
            classes(
                "w-16", "h-16", "bg-nano-primary/10", "text-nano-primary",
                "rounded-2xl", "flex", "items-center", "justify-center", "text-3xl",
                "group-hover:scale-110", "transition-transform"
            )
        }) {
            val iconName = when (category.name.lowercase()) {
                "doručak" -> "egg_alt"
                "ručak" -> "soup_kitchen"
                "večera" -> "dinner_dining"
                "desert", "slatko" -> "cake"
                "užina" -> "bakery_dining"
                "predjelo" -> "local_pizza"
                else -> "restaurant"
            }
            MaterialIcon(iconName, "text-3xl")
        }

        H3(attrs = {
            classes("text-xl", "font-bold", "text-white", "w-full", "truncate")
            title(category.name) // Prikazuje puni naziv na hover
        }) {
            Text(category.name)
        }

        P(attrs = { classes("text-gray-500", "text-sm", "font-medium") }) {
            Text("Kliknite za pregled recepata")
        }
    }
}

@Composable
fun PendingRecipesContent(
    state: ResultState<List<Recipe>>,
    onView: (Recipe) -> Unit,
    onEdit: (Recipe) -> Unit,
    onDeleteRequest: (Recipe) -> Unit
) {
    Main(attrs = {
        classes(
            "flex-1",
            "overflow-y-auto",
            "no-scrollbar",
            "px-4",
            "py-8",
            "sm:p-8",
            "bg-nano-background/50"
        )
    }) {
        Div(attrs = { classes("max-w-7xl", "mx-auto") }) {
            when (state) {
                is ResultState.Loading -> {
                    RecipeSkeletonGrid()
                }

                is ResultState.Error -> {
                    Div(attrs = {
                        classes(
                            "bg-red-500/10",
                            "border",
                            "border-red-500/50",
                            "p-6",
                            "rounded-2xl",
                            "text-center"
                        )
                    }) {
                        P(attrs = {
                            classes(
                                "text-red-500",
                                "font-bold",
                                "text-lg"
                            )
                        }) { Text("Greška!") }
                        P(attrs = { classes("text-red-400", "mt-2") }) {
                            Text(
                                state.message ?: "Neuspješno učitavanje recepata."
                            )
                        }
                    }
                }

                is ResultState.Success -> {
                    if (state.data.isEmpty()) {
                        Div(attrs = {
                            classes(
                                "text-center",
                                "py-20",
                                "bg-nano-surface/30",
                                "rounded-3xl",
                                "border",
                                "border-dashed",
                                "border-white/10"
                            )
                        }) {
                            MaterialIcon("pending_actions", "text-6xl block mb-4 text-gray-600")
                            H3(attrs = {
                                classes(
                                    "text-xl",
                                    "font-bold"
                                )
                            }) { Text("Nema recepata na čekanju") }
                            P(attrs = {
                                classes(
                                    "text-gray-400",
                                    "mt-2"
                                )
                            }) { Text("Svi AI generisani recepti su pregledani.") }
                        }
                    } else {
                        RecipeTable(state.data, onView, onEdit, onDeleteRequest)
                    }
                }
            }
        }
    }
}


@Composable
fun AdminDashboard() {
    var activeRecipesState by remember { mutableStateOf<ResultState<List<Recipe>>>(ResultState.Loading) }
    var pendingRecipesState by remember { mutableStateOf<ResultState<List<Recipe>>>(ResultState.Loading) }
    var categoriesState by remember { mutableStateOf<ResultState<List<Category>>>(ResultState.Loading) }
    var currentScreen by remember { mutableStateOf<Screen>(Screen.Dashboard) }
    var recipeToDelete by remember { mutableStateOf<Recipe?>(null) }
    var isAddCategoryModalOpen by remember { mutableStateOf(false) }
    var categoryToEdit by remember { mutableStateOf<Category?>(null) }
    var categoryToDelete by remember { mutableStateOf<Category?>(null) }
    var isMobileMenuOpen by remember { mutableStateOf(false) }

    fun loadActiveRecipes() {
        RecipeService.getRecipes(activeOnly = true) { activeRecipesState = it }
    }

    fun loadPendingRecipes() {
        RecipeService.getRecipes(activeOnly = false) { pendingRecipesState = it }
    }

    fun loadCategories() {
        RecipeService.getCategories { categoriesState = it }
    }

    LaunchedEffect(Unit) {
        loadActiveRecipes()
        loadPendingRecipes()
        loadCategories()
    }

    Div(attrs = { classes("flex", "h-screen", "bg-nano-background", "text-white", "relative") }) {
        // Overlay for mobile menu
        if (isMobileMenuOpen) {
            Div(attrs = {
                classes("fixed", "inset-0", "bg-black/60", "backdrop-blur-sm", "z-40", "lg:hidden")
                onClick { isMobileMenuOpen = false }
            })
        }

        Sidebar(
            currentScreen = currentScreen,
            isOpen = isMobileMenuOpen,
            onNavigate = {
                currentScreen = it
                if (it is Screen.Dashboard) loadActiveRecipes()
                if (it is Screen.PendingRecipes) loadPendingRecipes()
                isMobileMenuOpen = false
            }
        )

        Div(attrs = { classes("flex-1", "flex", "flex-col", "overflow-hidden") }) {
            when (val screen = currentScreen) {
                is Screen.Dashboard -> {
                    DashboardHeader(
                        title = "Aktivni Recepti",
                        subtitle = "Pregledajte i uređujte aktivne recepte",
                        onRefresh = { loadActiveRecipes() },
                        onAddRecipe = { currentScreen = Screen.AddRecipe },
                        onMenuClick = { isMobileMenuOpen = !isMobileMenuOpen }
                    )

                    MainContent(
                        state = activeRecipesState,
                        onView = { currentScreen = Screen.Details(it) },
                        onEdit = { currentScreen = Screen.EditRecipe(it) },
                        onDeleteRequest = { recipeToDelete = it }
                    )
                }

                is Screen.PendingRecipes -> {
                    DashboardHeader(
                        title = "Recepti na čekanju",
                        subtitle = "AI generisani recepti koji čekaju objavu",
                        onRefresh = { loadPendingRecipes() },
                        onAddRecipe = null,
                        onMenuClick = { isMobileMenuOpen = !isMobileMenuOpen }
                    )

                    PendingRecipesContent(
                        state = pendingRecipesState,
                        onView = { currentScreen = Screen.Details(it) },
                        onEdit = { currentScreen = Screen.EditRecipe(it) },
                        onDeleteRequest = { recipeToDelete = it }
                    )
                }

                is Screen.Categories -> {
                    DashboardHeader(
                        title = "Kategorije",
                        subtitle = "Pregled svih dostupnih kategorija jela",
                        onRefresh = { loadCategories() },
                        onAddRecipe = null,
                        onMenuClick = { isMobileMenuOpen = !isMobileMenuOpen }
                    )
                    AdminCategoriesPage(
                        state = categoriesState,
                        onAddCategory = { isAddCategoryModalOpen = true },
                        onEditCategory = { categoryToEdit = it },
                        onDeleteCategory = { categoryToDelete = it }
                    )
                }

                is Screen.Details -> {
                    RecipeDetailsPage(
                        recipe = screen.recipe,
                        onBack = { currentScreen = Screen.Dashboard }
                    )
                }

                is Screen.AddRecipe -> {
                    AddRecipePage(
                        categoriesState = categoriesState,
                        onBack = { currentScreen = Screen.Dashboard },
                        onSuccess = {
                            loadActiveRecipes()
                            currentScreen = Screen.Dashboard
                        }
                    )
                }

                is Screen.EditRecipe -> {
                    EditRecipePage(
                        recipe = screen.recipe,
                        categoriesState = categoriesState,
                        onBack = {
                            currentScreen =
                                if (screen.recipe.active) Screen.Dashboard else Screen.PendingRecipes
                        },
                        onSuccess = {
                            loadActiveRecipes()
                            loadPendingRecipes()
                            currentScreen =
                                if (screen.recipe.active) Screen.Dashboard else Screen.PendingRecipes
                        }
                    )
                }
            }
        }

        recipeToDelete?.let { recipe ->
            DeleteConfirmationModal(
                recipeName = recipe.title,
                onConfirm = {
                    RecipeService.deleteRecipe(recipe.id) { result ->
                        if (result is ResultState.Success) {
                            loadActiveRecipes()
                            loadPendingRecipes()
                        }
                    }
                    recipeToDelete = null
                },
                onCancel = { recipeToDelete = null }
            )
        }

        if (isAddCategoryModalOpen) {
            AddCategoryModal(
                onConfirm = { categoryName ->
                    RecipeService.addCategory(Category("", categoryName)) { result ->
                        if (result is ResultState.Success) {
                            loadCategories()
                            isAddCategoryModalOpen = false
                        }
                    }
                },
                onCancel = { isAddCategoryModalOpen = false }
            )
        }

        categoryToEdit?.let { category ->
            EditCategoryModal(
                category = category,
                onConfirm = { updatedCategory ->
                    RecipeService.updateCategory(updatedCategory) { result ->
                        if (result is ResultState.Success) {
                            loadCategories()
                            categoryToEdit = null
                        }
                    }
                },
                onCancel = { categoryToEdit = null }
            )
        }

        categoryToDelete?.let { category ->
            DeleteCategoryConfirmationModal(
                categoryName = category.name,
                onConfirm = {
                    RecipeService.deleteCategory(category.id) { result ->
                        if (result is ResultState.Success) {
                            loadCategories()
                            categoryToDelete = null
                        }
                    }
                },
                onCancel = { categoryToDelete = null }
            )
        }
    }
}

@Composable
fun Sidebar(currentScreen: Screen, isOpen: Boolean, onNavigate: (Screen) -> Unit) {
    Div(attrs = {
        classes(
            "fixed",
            "lg:relative",
            "inset-y-0",
            "left-0",
            "z-50",
            "w-64",
            "bg-nano-surface",
            "shadow-xl",
            "flex",
            "flex-col",
            "border-r",
            "border-white/5",
            "transition-transform",
            "duration-300",
            "ease-in-out"
        )
        if (!isOpen) classes("-translate-x-full", "lg:translate-x-0") else classes("translate-x-0")
    }) {
        // Logo
        Div(attrs = { classes("p-8", "flex", "items-center", "gap-4") }) {
            Img(src = "favicon.png", attrs = { classes("w-10", "h-10", "rounded-xl", "shadow-lg") })
            H1(attrs = { classes("text-2xl", "font-black", "tracking-tight", "text-white") }) {
                Text("ŠAFRAN ADMIN")
            }
        }

        // Navigation
        Nav(attrs = { classes("flex-1", "px-4", "py-6", "space-y-2") }) {
            SidebarLink("restaurant_menu", "Svi Recepti", currentScreen is Screen.Dashboard) {
                onNavigate(Screen.Dashboard)
            }
            SidebarLink("pending", "Recepti na čekanju", currentScreen is Screen.PendingRecipes) {
                onNavigate(Screen.PendingRecipes)
            }
            SidebarLink("category", "Kategorije", currentScreen is Screen.Categories) {
                onNavigate(Screen.Categories)
            }
            SidebarLink("group", "Korisnici", false) {}
            SidebarLink("settings", "Postavke", false) {}
        }

        Div(attrs = { classes("p-6", "border-t", "border-white/5") }) {
            Div(attrs = {
                classes(
                    "bg-nano-primary/10",
                    "p-4",
                    "rounded-xl",
                    "border",
                    "border-nano-primary/20"
                )
            }) {
                P(attrs = {
                    classes(
                        "text-xs",
                        "text-nano-primary",
                        "font-bold",
                        "uppercase",
                        "mb-1"
                    )
                }) { Text("Status Servera") }
                P(attrs = {
                    classes(
                        "text-sm",
                        "text-green-400",
                        "flex",
                        "items-center",
                        "gap-2"
                    )
                }) {
                    Span(attrs = { classes("w-2", "h-2", "bg-green-400", "rounded-full") })
                    Text("Online")
                }
            }
        }
    }
}

@Composable
fun SidebarLink(icon: String, text: String, active: Boolean, onClick: () -> Unit) {
    val activeClasses = if (active) {
        listOf("bg-nano-primary", "text-black", "shadow-lg", "shadow-nano-primary/20", "font-bold")
    } else {
        listOf("text-gray-400", "hover:bg-white/5", "hover:text-white")
    }

    A(href = "#", attrs = {
        classes(
            "flex",
            "items-center",
            "px-4",
            "py-3",
            "rounded-xl",
            "transition-all",
            "duration-200"
        )
        classes(activeClasses)
        onClick {
            it.preventDefault()
            onClick()
        }
    }) {
        MaterialIcon(icon, "mr-4 text-xl")
        Text(text)
    }
}

@Composable
fun DashboardHeader(
    title: String,
    subtitle: String,
    onRefresh: () -> Unit,
    onAddRecipe: (() -> Unit)? = null,
    onBack: (() -> Unit)? = null,
    onMenuClick: (() -> Unit)? = null,
    onSaveAction: (() -> Unit)? = null,
    isSubmitting: Boolean = false,
    saveButtonText: String = "SAČUVAJ"
) {
    Header(attrs = {
        classes(
            "bg-nano-surface/50",
            "backdrop-blur-md",
            "border-b",
            "border-white/5",
            "sticky",
            "top-0",
            "z-20"
        )
    }) {
        Div(attrs = {
            classes(
                "max-w-7xl",
                "mx-auto",
                "px-4",
                "sm:px-8",
                "py-4",
                "sm:py-6",
                "flex",
                "justify-between",
                "items-center"
            )
        }) {
            Div(attrs = { classes("flex", "items-center", "gap-3", "sm:gap-6") }) {
                // Hamburger Button for mobile
                if (onMenuClick != null) {
                    Button(attrs = {
                        classes(
                            "lg:hidden",
                            "p-2",
                            "bg-white/5",
                            "hover:bg-white/10",
                            "rounded-xl",
                            "transition-all"
                        )
                        onClick { onMenuClick() }
                    }) {
                        MaterialIcon("menu")
                    }
                }

                if (onBack != null) {
                    Button(attrs = {
                        classes(
                            "p-3",
                            "bg-white/5",
                            "hover:bg-white/10",
                            "rounded-xl",
                            "transition-all",
                            "border",
                            "border-white/10",
                            "flex",
                            "items-center",
                            "gap-2"
                        )
                        onClick { onBack() }
                    }) {
                        MaterialIcon("arrow_back")
                        Text("Nazad")
                    }
                }
                Div {
                    H2(attrs = {
                        classes(
                            "text-xl",
                            "sm:text-3xl",
                            "font-extrabold",
                            "text-white",
                            "tracking-tight",
                            "truncate",
                            "max-w-[150px]",
                            "sm:max-w-none"
                        )
                    }) {
                        Text(title)
                    }
                    P(attrs = {
                        classes(
                            "text-gray-400",
                            "text-xs",
                            "sm:text-sm",
                            "mt-1",
                            "hidden",
                            "sm:block"
                        )
                    }) {
                        Text(subtitle)
                    }
                }
            }

            Div(attrs = { classes("flex", "gap-2", "sm:gap-4") }) {
                Button(attrs = {
                    classes(
                        "bg-white/5",
                        "hover:bg-white/10",
                        "text-white",
                        "p-3",
                        "sm:px-6",
                        "sm:py-3",
                        "rounded-xl",
                        "font-bold",
                        "transition-all",
                        "flex",
                        "items-center",
                        "gap-2",
                        "border",
                        "border-white/10"
                    )
                    onClick { onRefresh() }
                }) {
                    MaterialIcon("refresh")
                    Span(attrs = { classes("hidden", "sm:inline") }) { Text("Osvježi") }
                }

                if (onAddRecipe != null) {
                    Button(attrs = {
                        classes(
                            "bg-nano-primary",
                            "hover:bg-yellow-400",
                            "text-black",
                            "p-3",
                            "sm:px-6",
                            "sm:py-3",
                            "rounded-xl",
                            "font-bold",
                            "shadow-lg",
                            "shadow-nano-primary/30",
                            "transition-all",
                            "flex",
                            "items-center",
                            "gap-2"
                        )
                        onClick { onAddRecipe() }
                    }) {
                        MaterialIcon("add_circle")
                        Span(attrs = { classes("hidden", "sm:inline") }) { Text("Novi Recept") }
                    }
                }

                if (onSaveAction != null) {
                    Button(attrs = {
                        classes(
                            "bg-blue-500",
                            "hover:bg-blue-600",
                            "text-white",
                            "p-3",
                            "sm:px-6",
                            "sm:py-3",
                            "rounded-xl",
                            "font-bold",
                            "shadow-lg",
                            "shadow-blue-500/30",
                            "transition-all",
                            "flex",
                            "items-center",
                            "gap-2"
                        )
                        if (isSubmitting) classes("opacity-50", "cursor-not-allowed")
                        onClick { if (!isSubmitting) onSaveAction() }
                    }) {
                        MaterialIcon(if (isSubmitting) "hourglass_empty" else "save")
                        Span(attrs = { classes("hidden", "sm:inline") }) { Text(saveButtonText) }
                    }
                }
            }
        }
    }
}

@Composable
fun MainContent(
    state: ResultState<List<Recipe>>,
    onView: (Recipe) -> Unit,
    onEdit: (Recipe) -> Unit,
    onDeleteRequest: (Recipe) -> Unit
) {
    Main(attrs = {
        classes(
            "flex-1",
            "overflow-y-auto",
            "no-scrollbar",
            "px-4",
            "py-8",
            "sm:p-8",
            "bg-nano-background/50"
        )
    }) {
        Div(attrs = { classes("max-w-7xl", "mx-auto") }) {
            when (state) {
                is ResultState.Loading -> {
                    RecipeSkeletonGrid()
                }

                is ResultState.Error -> {
                    Div(attrs = {
                        classes(
                            "bg-red-500/10",
                            "border",
                            "border-red-500/50",
                            "p-6",
                            "rounded-2xl",
                            "text-center"
                        )
                    }) {
                        P(attrs = {
                            classes(
                                "text-red-500",
                                "font-bold",
                                "text-lg"
                            )
                        }) { Text("Greška!") }
                        P(attrs = { classes("text-red-400", "mt-2") }) {
                            Text(
                                state.message ?: "Neuspješno učitavanje recepata."
                            )
                        }
                    }
                }

                is ResultState.Success -> {
                    if (state.data.isEmpty()) {
                        Div(attrs = {
                            classes(
                                "text-center",
                                "py-20",
                                "bg-nano-surface/30",
                                "rounded-3xl",
                                "border",
                                "border-dashed",
                                "border-white/10"
                            )
                        }) {
                            MaterialIcon("layers_clear", "text-6xl block mb-4 text-gray-600")
                            H3(attrs = {
                                classes(
                                    "text-xl",
                                    "font-bold"
                                )
                            }) { Text("Nema pronađenih recepata") }
                            P(attrs = {
                                classes(
                                    "text-gray-400",
                                    "mt-2"
                                )
                            }) { Text("Započnite dodavanjem prvog recepta u bazu.") }
                        }
                    } else {
                        RecipeTable(state.data, onView, onEdit, onDeleteRequest)
                    }
                }
            }
        }
    }
}

@Composable
fun RecipeTable(
    recipes: List<Recipe>,
    onView: (Recipe) -> Unit,
    onEdit: (Recipe) -> Unit,
    onDeleteRequest: (Recipe) -> Unit
) {
    // Mobile Card View
    Div(attrs = { classes("lg:hidden", "space-y-4") }) {
        recipes.forEach { recipe ->
            Div(attrs = {
                classes(
                    "bg-nano-surface",
                    "rounded-3xl",
                    "p-4",
                    "border",
                    "border-white/5",
                    "flex",
                    "flex-col",
                    "gap-4"
                )
            }) {
                Div(attrs = { classes("flex", "gap-4") }) {
                    Img(
                        src = recipe.imageUrl,
                        attrs = {
                            classes(
                                "w-20",
                                "h-20",
                                "rounded-2xl",
                                "object-cover",
                                "shadow-xl",
                                "bg-nano-background"
                            )
                        }
                    )
                    Div(attrs = { classes("flex-1", "min-w-0") }) {
                        Div(attrs = {
                            classes(
                                "text-lg",
                                "font-bold",
                                "text-white",
                                "truncate"
                            )
                        }) { Text(recipe.title) }
                        Span(attrs = {
                            classes(
                                "inline-block",
                                "mt-2",
                                "px-3",
                                "py-1",
                                "bg-nano-sand/20",
                                "text-nano-sand",
                                "text-[10px]",
                                "font-black",
                                "uppercase",
                                "rounded-full",
                                "max-w-full",
                                "truncate"
                            )
                        }) {
                            Text(recipe.category)
                        }
                    }
                }

                Div(attrs = {
                    classes(
                        "flex",
                        "justify-between",
                        "items-center",
                        "pt-4",
                        "border-t",
                        "border-white/5"
                    )
                }) {
                    Div(attrs = { classes("flex", "gap-3") }) {
                        Div(attrs = { classes("flex", "items-center", "gap-1") }) {
                            MaterialIcon("schedule", "text-xs text-gray-400")
                            Span(attrs = { classes("text-xs") }) { Text(recipe.prepTime) }
                        }
                        Div(attrs = { classes("flex", "items-center", "gap-1") }) {
                            MaterialIcon("star", "text-nano-primary text-xs")
                            Span(attrs = {
                                classes(
                                    "text-xs",
                                    "font-bold"
                                )
                            }) { Text(recipe.rating.toString()) }
                        }
                    }

                    Div(attrs = { classes("flex", "gap-2") }) {
                        Button(attrs = {
                            classes("p-2", "bg-nano-primary/10", "text-nano-primary", "rounded-lg")
                            onClick { onView(recipe) }
                        }) { MaterialIcon("visibility", "text-sm") }
                        Button(attrs = {
                            classes("p-2", "bg-blue-500/10", "text-blue-400", "rounded-lg")
                            onClick { onEdit(recipe) }
                        }) { MaterialIcon("edit", "text-sm") }
                        Button(attrs = {
                            classes("p-2", "bg-red-500/10", "text-red-400", "rounded-lg")
                            onClick { onDeleteRequest(recipe) }
                        }) { MaterialIcon("delete", "text-sm") }
                    }
                }
            }
        }
    }

    // Desktop Table View
    Div(attrs = {
        classes(
            "hidden",
            "lg:block",
            "bg-nano-surface",
            "rounded-3xl",
            "shadow-2xl",
            "overflow-x-auto",
            "border",
            "border-white/5",
            "no-scrollbar"
        )
    }) {
        Table(attrs = { classes("min-w-full", "divide-y", "divide-white/5") }) {
            Thead(attrs = { classes("bg-white/[0.02]") }) {
                Tr {
                    TableHeader("Recept")
                    TableHeader("Kategorija")
                    TableHeader("Vrijeme & Nivo")
                    TableHeader("Rating")
                    TableHeader("Promoted")
                    TableHeader("Akcije")
                }
            }
            Tbody(attrs = { classes("divide-y", "divide-white/5") }) {
                recipes.forEach { recipe ->
                    Tr(attrs = { classes("hover:bg-white/[0.02]", "transition-colors") }) {
                        // Recept info card-style in row
                        Td(attrs = { classes("px-4", "lg:px-6", "py-6", "max-w-[300px]") }) {
                            Div(attrs = { classes("flex", "items-center", "gap-4") }) {
                                Img(
                                    src = recipe.imageUrl,
                                    alt = recipe.title,
                                    attrs = {
                                        classes(
                                            "w-20",
                                            "h-20",
                                            "rounded-2xl",
                                            "object-cover",
                                            "shadow-xl",
                                            "bg-nano-background",
                                            "flex-shrink-0"
                                        )
                                    }
                                )
                                Div(attrs = { classes("min-w-0") }) {
                                    Div(attrs = {
                                        classes(
                                            "text-lg",
                                            "font-bold",
                                            "text-white",
                                            "truncate"
                                        )
                                    }) { Text(recipe.title) }
                                    Div(attrs = {
                                        classes(
                                            "text-sm",
                                            "text-gray-400",
                                            "truncate"
                                        )
                                    }) { Text(recipe.description) }
                                }
                            }
                        }

                        // Kategorija
                        Td(attrs = { classes("px-4", "lg:px-6", "py-6") }) {
                            Span(attrs = {
                                classes(
                                    "inline-block",
                                    "px-4",
                                    "py-1.5",
                                    "bg-nano-sand/20",
                                    "text-nano-sand",
                                    "text-xs",
                                    "font-black",
                                    "uppercase",
                                    "tracking-widest",
                                    "rounded-full",
                                    "border",
                                    "border-nano-sand/20",
                                    "max-w-[120px]",
                                    "truncate"
                                )
                                title(recipe.category)
                            }) {
                                Text(recipe.category)
                            }
                        }

                        // Vrijeme i Nivo
                        Td(attrs = { classes("px-4", "lg:px-6", "py-6") }) {
                            Div(attrs = { classes("flex", "flex-col", "gap-1") }) {
                                Div(attrs = {
                                    classes(
                                        "text-sm",
                                        "flex",
                                        "items-center",
                                        "gap-2"
                                    )
                                }) {
                                    MaterialIcon("schedule", "text-sm text-gray-400")
                                    Text(recipe.prepTime)
                                }
                                Div(attrs = {
                                    classes(
                                        "text-xs",
                                        "font-bold",
                                        "text-nano-olive",
                                        "uppercase"
                                    )
                                }) {
                                    Text(recipe.level)
                                }
                            }
                        }

                        // Rating
                        Td(attrs = { classes("px-4", "lg:px-6", "py-6") }) {
                            Div(attrs = { classes("flex", "items-center", "gap-1") }) {
                                MaterialIcon("star", "text-nano-primary text-sm")
                                Span(attrs = { classes("font-bold") }) { Text(recipe.rating.toString()) }
                            }
                        }

                        // Promoted
                        Td(attrs = { classes("px-4", "lg:px-6", "py-6") }) {
                            Input(type = InputType.Checkbox, attrs = {
                                classes(
                                    "w-5",
                                    "h-5",
                                    "rounded",
                                    "accent-nano-primary",
                                    "cursor-pointer"
                                )
                                checked(recipe.promoted ?: false)
                                onChange { event ->
                                    RecipeService.togglePromoted(recipe.id, event.value) { result ->
                                        // Handle result if needed
                                    }
                                }
                            })
                        }

                        // Akcije
                        Td(attrs = { classes("px-4", "lg:px-6", "py-6") }) {
                            Div(attrs = { classes("flex", "gap-3") }) {
                                Button(attrs = {
                                    classes(
                                        "p-3",
                                        "bg-nano-primary/10",
                                        "hover:bg-nano-primary/20",
                                        "text-nano-primary",
                                        "rounded-xl",
                                        "transition-all",
                                        "flex",
                                        "items-center"
                                    )
                                    title("Pogledaj")
                                    onClick { onView(recipe) }
                                }) {
                                    MaterialIcon("visibility")
                                }
                                Button(attrs = {
                                    classes(
                                        "p-3",
                                        "bg-blue-500/10",
                                        "hover:bg-blue-500/20",
                                        "text-blue-400",
                                        "rounded-xl",
                                        "transition-all",
                                        "flex",
                                        "items-center"
                                    )
                                    title("Uredi")
                                    onClick { onEdit(recipe) }
                                }) {
                                    MaterialIcon("edit")
                                }
                                Button(attrs = {
                                    classes(
                                        "p-3",
                                        "bg-red-500/10",
                                        "hover:bg-red-500/20",
                                        "text-red-400",
                                        "rounded-xl",
                                        "transition-all",
                                        "flex",
                                        "items-center"
                                    )
                                    title("Obriši")
                                    onClick { onDeleteRequest(recipe) }
                                }) {
                                    MaterialIcon("delete")
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun TableHeader(text: String) {
    Th(attrs = {
        classes(
            "px-4",
            "lg:px-6",
            "py-4",
            "text-left",
            "text-xs",
            "font-black",
            "text-gray-500",
            "uppercase",
            "tracking-wider"
        )
    }) {
        Text(text)
    }
}

@Composable
fun RecipeDetailsPage(recipe: Recipe, onBack: () -> Unit) {
    // Header za detalje sa Back dugmetom
    DashboardHeader(
        title = recipe.title,
        subtitle = "Detaljan pregled recepta",
        onRefresh = { /* Nothing to refresh here */ },
        onAddRecipe = null,
        onBack = onBack
    )

    Main(attrs = {
        classes(
            "flex-1",
            "overflow-y-auto",
            "no-scrollbar",
            "px-4",
            "py-8",
            "sm:p-8",
            "bg-nano-background/50"
        )
    }) {
        Div(attrs = { classes("max-w-5xl", "mx-auto") }) {
            // Hero section sa slikom
            Div(attrs = {
                classes(
                    "relative",
                    "h-64",
                    "sm:h-[400px]",
                    "w-full",
                    "rounded-3xl",
                    "overflow-hidden",
                    "shadow-2xl",
                    "mb-8",
                    "sm:mb-12"
                )
            }) {
                Img(src = recipe.imageUrl, attrs = { classes("w-full", "h-full", "object-cover") })
                Div(attrs = {
                    classes(
                        "absolute",
                        "inset-0",
                        "bg-gradient-to-t",
                        "from-nano-background",
                        "via-transparent",
                        "to-transparent"
                    )
                })

                Div(attrs = { classes("absolute", "bottom-8", "left-8", "right-8") }) {
                    Span(attrs = {
                        classes(
                            "inline-block",
                            "px-4",
                            "py-2",
                            "bg-nano-primary",
                            "text-black",
                            "text-sm",
                            "font-black",
                            "rounded-full",
                            "uppercase",
                            "max-w-full",
                            "truncate"
                        )
                    }) {
                        Text(recipe.category)
                    }
                }
            }

            // Info Grid
            Div(attrs = {
                classes(
                    "grid",
                    "grid-cols-1",
                    "sm:grid-cols-2",
                    "lg:grid-cols-3",
                    "gap-4",
                    "sm:gap-8",
                    "mb-12"
                )
            }) {
                DetailCard("schedule", "Koristi se", recipe.prepTime, "text-blue-400")
                DetailCard("signal_cellular_alt", "Težina", recipe.level, "text-nano-olive")
                DetailCard("star", "Ocjena", "${recipe.rating} / 5.0", "text-nano-primary")
            }

            // Description
            Div(attrs = {
                classes(
                    "bg-nano-surface",
                    "p-6",
                    "sm:p-8",
                    "rounded-3xl",
                    "border",
                    "border-white/5",
                    "mb-12"
                )
            }) {
                H3(attrs = {
                    classes(
                        "text-xl",
                        "font-bold",
                        "mb-4",
                        "text-gray-500"
                    )
                }) { Text("Opis recepta") }
                P(attrs = {
                    classes(
                        "text-lg",
                        "sm:text-xl",
                        "text-gray-200",
                        "leading-relaxed"
                    )
                }) { Text(recipe.description) }
            }

            // Sastojci i Priprema
            Div(attrs = {
                classes(
                    "grid",
                    "grid-cols-1",
                    "md:grid-cols-2",
                    "gap-8",
                    "sm:gap-12"
                )
            }) {
                Div(attrs = {
                    classes(
                        "bg-nano-surface/50",
                        "p-8",
                        "rounded-3xl",
                        "border",
                        "border-white/5"
                    )
                }) {
                    H3(attrs = {
                        classes(
                            "text-2xl",
                            "font-bold",
                            "mb-8",
                            "flex",
                            "items-center",
                            "gap-3"
                        )
                    }) {
                        MaterialIcon("shopping_basket", "text-nano-primary text-3xl")
                        Text("Sastojci")
                    }
                    Ul(attrs = { classes("space-y-4") }) {
                        recipe.ingredients.forEach { ingredient ->
                            Li(attrs = {
                                classes(
                                    "flex",
                                    "justify-between",
                                    "items-center",
                                    "p-4",
                                    "bg-white/5",
                                    "rounded-2xl",
                                    "border",
                                    "border-white/5"
                                )
                            }) {
                                Span(attrs = { classes("text-gray-300", "font-medium") }) {
                                    Text(
                                        ingredient.name
                                    )
                                }
                                Span(attrs = {
                                    classes(
                                        "px-3",
                                        "py-1",
                                        "bg-nano-primary/10",
                                        "text-nano-primary",
                                        "font-black",
                                        "rounded-lg"
                                    )
                                }) {
                                    Text(ingredient.amount)
                                }
                            }
                        }
                    }
                }

                Div(attrs = {
                    classes(
                        "bg-nano-surface/50",
                        "p-8",
                        "rounded-3xl",
                        "border",
                        "border-white/5"
                    )
                }) {
                    H3(attrs = {
                        classes(
                            "text-2xl",
                            "font-bold",
                            "mb-8",
                            "flex",
                            "items-center",
                            "gap-3"
                        )
                    }) {
                        MaterialIcon("soup_kitchen", "text-nano-primary text-3xl")
                        Text("Priprema")
                    }
                    Div(attrs = { classes("space-y-8") }) {
                        recipe.instructions.forEach { instruction ->
                            Div(attrs = { classes("flex", "gap-6") }) {
                                Div(attrs = {
                                    classes(
                                        "flex-shrink-0",
                                        "w-10",
                                        "h-10",
                                        "bg-nano-primary",
                                        "text-black",
                                        "font-black",
                                        "rounded-xl",
                                        "flex",
                                        "items-center",
                                        "justify-center",
                                        "shadow-lg",
                                        "shadow-nano-primary/20",
                                        "rotate-3"
                                    )
                                }) {
                                    Text(instruction.step.toString())
                                }
                                P(attrs = {
                                    classes(
                                        "text-gray-300",
                                        "leading-relaxed",
                                        "text-lg"
                                    )
                                }) {
                                    Text(instruction.description)
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun DeleteConfirmationModal(recipeName: String, onConfirm: () -> Unit, onCancel: () -> Unit) {
    Div(attrs = {
        classes(
            "fixed",
            "inset-0",
            "z-[100]",
            "flex",
            "items-center",
            "justify-center",
            "p-4",
            "bg-black/90",
            "backdrop-blur-sm"
        )
        onClick { onCancel() }
    }) {
        Div(attrs = {
            classes(
                "bg-nano-surface",
                "w-full",
                "max-w-md",
                "rounded-3xl",
                "p-8",
                "shadow-2xl",
                "border",
                "border-white/10",
                "text-center"
            )
            onClick { it.stopPropagation() }
        }) {
            Div(attrs = {
                classes(
                    "w-20",
                    "h-20",
                    "bg-red-500/10",
                    "text-red-500",
                    "rounded-full",
                    "flex",
                    "items-center",
                    "justify-center",
                    "mx-auto",
                    "mb-6"
                )
            }) {
                MaterialIcon("warning", "text-4xl")
            }

            H3(attrs = { classes("text-2xl", "font-bold", "mb-2") }) { Text("Brisanje Recepta") }

            P(attrs = { classes("text-gray-400", "mb-8", "leading-relaxed") }) {
                Text("Da li ste sigurni da želite trajno obrisati ")
                B(attrs = { classes("text-white") }) { Text(recipeName) }
                Text("? Ova akcija se ne može poništiti.")
            }

            Div(attrs = { classes("flex", "gap-4") }) {
                Button(attrs = {
                    classes(
                        "flex-1",
                        "py-4",
                        "bg-white/5",
                        "hover:bg-white/10",
                        "text-white",
                        "font-bold",
                        "rounded-2xl",
                        "transition-all"
                    )
                    onClick { onCancel() }
                }) {
                    Text("Poništi")
                }

                Button(attrs = {
                    classes(
                        "flex-1",
                        "py-4",
                        "bg-red-500",
                        "hover:bg-red-600",
                        "text-white",
                        "font-bold",
                        "rounded-2xl",
                        "shadow-lg",
                        "shadow-red-500/20",
                        "transition-all"
                    )
                    onClick { onConfirm() }
                }) {
                    Text("Obriši")
                }
            }
        }
    }
}

@Composable
fun AddCategoryModal(onConfirm: (String) -> Unit, onCancel: () -> Unit) {
    var categoryName by remember { mutableStateOf("") }

    Div(attrs = {
        classes(
            "fixed",
            "inset-0",
            "z-[100]",
            "flex",
            "items-center",
            "justify-center",
            "p-4",
            "bg-black/90",
            "backdrop-blur-sm"
        )
        onClick { onCancel() }
    }) {
        Div(attrs = {
            classes(
                "bg-nano-surface",
                "w-full",
                "max-w-md",
                "rounded-3xl",
                "p-8",
                "shadow-2xl",
                "border",
                "border-white/10"
            )
            onClick { it.stopPropagation() }
        }) {
            H3(attrs = {
                classes(
                    "text-2xl",
                    "font-bold",
                    "mb-6",
                    "text-center"
                )
            }) { Text("Nova Kategorija") }

            Div(attrs = { classes("mb-8") }) {
                AdminFormField("Naziv Kategorije") {
                    Input(type = InputType.Text, attrs = {
                        classes(
                            "w-full",
                            "bg-white/5",
                            "border",
                            "border-white/10",
                            "rounded-xl",
                            "p-4",
                            "text-white",
                            "focus:border-nano-primary",
                            "outline-none"
                        )
                        value(categoryName)
                        onInput { categoryName = it.value }
                        placeholder("npr. Italijanska kuhinja")
                    })
                }
            }

            Div(attrs = { classes("flex", "gap-4") }) {
                Button(attrs = {
                    classes(
                        "flex-1",
                        "py-4",
                        "bg-white/5",
                        "hover:bg-white/10",
                        "text-white",
                        "font-bold",
                        "rounded-2xl",
                        "transition-all"
                    )
                    onClick { onCancel() }
                }) {
                    Text("Otkaži")
                }

                Button(attrs = {
                    classes(
                        "flex-1",
                        "py-4",
                        "bg-nano-primary",
                        "hover:bg-yellow-400",
                        "text-black",
                        "font-bold",
                        "rounded-2xl",
                        "shadow-lg",
                        "shadow-nano-primary/20",
                        "transition-all"
                    )
                    onClick { if (categoryName.isNotBlank()) onConfirm(categoryName) }
                }) {
                    Text("Dodaj")
                }
            }
        }
    }
}

@Composable
fun AddRecipePage(
    categoriesState: ResultState<List<Category>>,
    onBack: () -> Unit,
    onSuccess: () -> Unit
) {
    var title by remember { mutableStateOf("") }
    var description by remember { mutableStateOf("") }
    var imageUrl by remember { mutableStateOf("") }
    var category by remember { mutableStateOf("") }
    var prepTime by remember { mutableStateOf("") }
    var level by remember { mutableStateOf("Lako") }
    var rating by remember { mutableStateOf(4.5) }
    var promoted by remember { mutableStateOf(false) }

    val ingredients = remember { mutableStateListOf<Ingredient>() }
    val instructions = remember { mutableStateListOf<Instruction>() }

    var isSubmitting by remember { mutableStateOf(false) }
    var isUploading by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }

    fun handleFileUpload(file: org.w3c.files.File) {
        val reader = FileReader()
        reader.onload = { event ->
            val arrayBuffer = event.target.asDynamic().result as? org.khronos.webgl.ArrayBuffer
            if (arrayBuffer != null) {
                val bytes = org.khronos.webgl.Int8Array(arrayBuffer).unsafeCast<ByteArray>()
                isUploading = true
                RecipeService.uploadImage(bytes, file.name) { result ->
                    isUploading = false
                    if (result is ResultState.Success) {
                        imageUrl = result.data
                    } else if (result is ResultState.Error) {
                        errorMessage = result.message ?: "Greška pri uploadu slike"
                    }
                }
            }
        }
        reader.readAsArrayBuffer(file)
    }

    fun submit() {
        if (title.isBlank() || description.isBlank()) {
            errorMessage = "Naslov i opis su obavezni!"
            return
        }
        isSubmitting = true
        val newRecipe = Recipe(
            id = "", // Server generates ID
            title = title,
            description = description,
            imageUrl = imageUrl,
            category = category,
            rating = rating,
            prepTime = prepTime,
            level = level,
            ingredients = ingredients.toList(),
            instructions = instructions.toList(),
            active = true,
            promoted = promoted
        )
        RecipeService.addRecipe(newRecipe) { result ->
            isSubmitting = false
            if (result is ResultState.Success<*>) {
                onSuccess()
            } else if (result is ResultState.Error) {
                errorMessage = result.message ?: "Greška pri spremanju recepta"
            }
        }
    }

    DashboardHeader(
        title = "Novi Recept",
        subtitle = "Dodajte novi kulinarski specijalitet u bazu",
        onRefresh = { /* Clear or reset if needed */ },
        onAddRecipe = null,
        onBack = onBack,
        onSaveAction = { submit() },
        isSubmitting = isSubmitting,
        saveButtonText = "OBJAVI RECEPT"
    )

    Main(attrs = {
        classes(
            "flex-1",
            "overflow-y-auto",
            "no-scrollbar",
            "p-8",
            "bg-nano-background/50"
        )
    }) {
        Div(attrs = { classes("max-w-4xl", "mx-auto", "space-y-8") }) {
            if (errorMessage != null) {
                Div(attrs = {
                    classes(
                        "bg-red-500/10",
                        "border",
                        "border-red-500/50",
                        "p-4",
                        "rounded-2xl",
                        "text-red-500",
                        "flex",
                        "justify-between",
                        "items-center"
                    )
                }) {
                    Text(errorMessage!!)
                    Button(attrs = { onClick { errorMessage = null } }) { MaterialIcon("close") }
                }
            }

            // Osnovne informacije
            AdminSectionCard("Osnovne Informacije") {
                AdminFormField("Naslov Recepta") {
                    Input(type = InputType.Text, attrs = {
                        classes(
                            "w-full",
                            "bg-white/5",
                            "border",
                            "border-white/10",
                            "rounded-xl",
                            "p-4",
                            "text-white",
                            "focus:border-nano-primary",
                            "outline-none"
                        )
                        value(title)
                        onInput { title = it.value }
                    })
                }

                AdminFormField("Opis") {
                    TextArea(attrs = {
                        classes(
                            "w-full",
                            "bg-white/5",
                            "border",
                            "border-white/10",
                            "rounded-xl",
                            "p-4",
                            "text-white",
                            "focus:border-nano-primary",
                            "outline-none",
                            "h-32"
                        )
                        value(description)
                        onInput { description = it.value }
                    })
                }

                Div(attrs = { classes("grid", "grid-cols-1", "sm:grid-cols-2", "gap-6") }) {
                    AdminFormField("Kategorija") {
                        Select(attrs = {
                            classes(
                                "w-full",
                                "bg-white/5",
                                "border",
                                "border-white/10",
                                "rounded-xl",
                                "h-[60px]",
                                "px-4",
                                "text-white",
                                "focus:border-nano-primary",
                                "outline-none",
                                "appearance-none",
                                "cursor-pointer",
                                "bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2020%2020%22%3E%3Cpath%20stroke%3D%22%239ca3af%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%221.5%22%20d%3D%22m6%208%204%204%204-4%22%2F%3E%3C%2Fsvg%3E')]",
                                "bg-[position:right_1.25rem_center]",
                                "bg-[length:1.25em_1.25em]",
                                "bg-no-repeat"
                            )
                            onInput { category = it.value ?: "" }
                        }) {
                            Option(
                                "",
                                attrs = { if (category == "") selected() }) { Text("Odaberi kategoriju") }
                            if (categoriesState is ResultState.Success) {
                                categoriesState.data.forEach { cat ->
                                    Option(value = cat.name, attrs = {
                                        if (cat.name == category) selected()
                                    }) {
                                        Text(cat.name)
                                    }
                                }
                            }
                        }
                    }
                    AdminFormField("Slika Recepta") {
                        Div(attrs = { classes("flex", "gap-4", "items-start") }) {
                            if (imageUrl.isNotBlank()) {
                                Img(
                                    src = imageUrl,
                                    attrs = {
                                        classes(
                                            "w-20",
                                            "h-20",
                                            "rounded-xl",
                                            "object-cover",
                                            "border",
                                            "border-white/10"
                                        )
                                    })
                            }
                            Div(attrs = { classes("flex-1", "relative") }) {
                                Input(type = InputType.File, attrs = {
                                    classes(
                                        "opacity-0",
                                        "absolute",
                                        "inset-0",
                                        "cursor-pointer",
                                        "z-10"
                                    )
                                    onChange {
                                        val file = it.target.files?.get(0)
                                        if (file != null) handleFileUpload(file)
                                    }
                                })
                                Div(attrs = {
                                    classes(
                                        "w-full",
                                        "bg-white/5",
                                        "border",
                                        "border-dashed",
                                        "border-white/20",
                                        "rounded-xl",
                                        "p-4",
                                        "text-center",
                                        "hover:border-nano-primary/50",
                                        "transition-all",
                                        "flex",
                                        "flex-col",
                                        "items-center",
                                        "gap-1"
                                    )
                                }) {
                                    if (isUploading) {
                                        Div(attrs = {
                                            classes(
                                                "w-5",
                                                "h-5",
                                                "border-2",
                                                "border-nano-primary",
                                                "border-t-transparent",
                                                "rounded-full",
                                                "animate-spin"
                                            )
                                        }) {}
                                        Text("Učitavanje...")
                                    } else {
                                        MaterialIcon("cloud_upload", "text-2xl text-gray-400")
                                        Text(if (imageUrl.isBlank()) "Izaberi sliku" else "Promijeni sliku")
                                    }
                                }
                            }
                        }
                        if (imageUrl.isNotBlank()) {
                            P(attrs = {
                                classes(
                                    "text-[10px]",
                                    "text-gray-500",
                                    "mt-1",
                                    "truncate"
                                )
                            }) {
                                Text(imageUrl)
                            }
                        }
                    }
                }

                Div(attrs = {
                    classes(
                        "grid",
                        "grid-cols-1",
                        "sm:grid-cols-2",
                        "lg:grid-cols-3",
                        "gap-6"
                    )
                }) {
                    AdminFormField("Vrijeme pripreme") {
                        Input(type = InputType.Text, attrs = {
                            classes(
                                "w-full",
                                "bg-white/5",
                                "border",
                                "border-white/10",
                                "rounded-xl",
                                "h-[60px]",
                                "px-4",
                                "text-white",
                                "focus:border-nano-primary",
                                "outline-none"
                            )
                            value(prepTime)
                            onInput { prepTime = it.value }
                            placeholder("npr. 45 min")
                        })
                    }
                    AdminFormField("Nivo") {
                        Select(attrs = {
                            classes(
                                "w-full",
                                "bg-white/5",
                                "border",
                                "border-white/10",
                                "rounded-xl",
                                "h-[60px]",
                                "px-4",
                                "text-white",
                                "focus:border-nano-primary",
                                "outline-none",
                                "appearance-none",
                                "cursor-pointer",
                                "bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2020%2020%22%3E%3Cpath%20stroke%3D%22%239ca3af%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%221.5%22%20d%3D%22m6%208%204%204%204-4%22%2F%3E%3C%2Fsvg%3E')]",
                                "bg-[position:right_1.25rem_center]",
                                "bg-[length:1.25em_1.25em]",
                                "bg-no-repeat"
                            )
                            onInput { level = it.value ?: "Lako" }
                        }) {
                            listOf("Lako", "Srednje", "Teško").forEach {
                                Option(value = it, attrs = {
                                    if (it == level) selected()
                                }) {
                                    Text(it)
                                }
                            }
                        }
                    }
                    AdminFormField("Rating (1-5)") {
                        Input(type = InputType.Number, attrs = {
                            classes(
                                "w-full",
                                "bg-white/5",
                                "border",
                                "border-white/10",
                                "rounded-xl",
                                "h-[60px]",
                                "px-4",
                                "text-white",
                                "focus:border-nano-primary",
                                "outline-none"
                            )
                            value(rating.toString())
                            onInput { rating = it.value?.toDouble() ?: 4.5 }
                            attr("min", "1")
                            attr("max", "5")
                            attr("step", "0.1")
                        })
                    }
                    Div(attrs = { classes("flex", "items-center", "gap-3", "h-[60px]", "pt-6") }) {
                        Input(type = InputType.Checkbox, attrs = {
                            classes(
                                "w-6",
                                "h-6",
                                "rounded",
                                "accent-nano-primary",
                                "cursor-pointer"
                            )
                            checked(promoted)
                            onChange { promoted = it.value }
                        })
                        Label(attrs = {
                            classes(
                                "text-sm",
                                "font-bold",
                                "text-gray-300"
                            )
                        }) { Text("Promovisano") }
                    }
                }
            }

            // Sastojci
            AdminSectionCard("Sastojci") {
                Div(attrs = { classes("space-y-4") }) {
                    ingredients.forEachIndexed { index, ingredient ->
                        Div(attrs = {
                            classes(
                                "flex",
                                "flex-col",
                                "sm:flex-row",
                                "gap-4",
                                "items-start",
                                "sm:items-center"
                            )
                        }) {
                            Div(attrs = { classes("flex-1", "w-full") }) {
                                AdminFormField("Naziv") {
                                    Input(type = InputType.Text, attrs = {
                                        classes(
                                            "w-full",
                                            "bg-white/5",
                                            "border",
                                            "border-white/10",
                                            "rounded-xl",
                                            "p-3",
                                            "text-white"
                                        )
                                        value(ingredient.name)
                                        onInput {
                                            ingredients[index] = ingredient.copy(name = it.value)
                                        }
                                    })
                                }
                            }
                            Div(attrs = { classes("w-full", "sm:w-48") }) {
                                AdminFormField("Količina") {
                                    Input(type = InputType.Text, attrs = {
                                        classes(
                                            "w-full",
                                            "bg-white/5",
                                            "border",
                                            "border-white/10",
                                            "rounded-xl",
                                            "p-3",
                                            "text-white"
                                        )
                                        value(ingredient.amount)
                                        onInput {
                                            ingredients[index] = ingredient.copy(amount = it.value)
                                        }
                                    })
                                }
                            }
                            Button(attrs = {
                                classes(
                                    "p-3",
                                    "bg-red-500/10",
                                    "text-red-500",
                                    "rounded-xl",
                                    "hover:bg-red-500/20",
                                    "sm:mt-4",
                                    "flex",
                                    "items-center",
                                    "self-end",
                                    "sm:self-auto"
                                )
                                onClick { ingredients.removeAt(index) }
                            }) { MaterialIcon("delete") }
                        }
                    }
                    Button(attrs = {
                        classes(
                            "w-full",
                            "py-3",
                            "border-2",
                            "border-dashed",
                            "border-white/10",
                            "rounded-2xl",
                            "text-gray-400",
                            "hover:border-nano-primary",
                            "hover:text-nano-primary",
                            "transition-all",
                            "flex",
                            "items-center",
                            "justify-center",
                            "gap-2"
                        )
                        onClick { ingredients.add(Ingredient("", "")) }
                    }) {
                        MaterialIcon("add")
                        Text("Dodaj Sastojak")
                    }
                }
            }

            // Koraci pripreme
            AdminSectionCard("Koraci Pripreme") {
                Div(attrs = { classes("space-y-6") }) {
                    instructions.forEachIndexed { index, instruction ->
                        Div(attrs = { classes("flex", "gap-4") }) {
                            Div(attrs = {
                                classes(
                                    "w-10",
                                    "h-10",
                                    "bg-nano-primary",
                                    "text-black",
                                    "font-black",
                                    "rounded-xl",
                                    "flex-shrink-0",
                                    "flex",
                                    "items-center",
                                    "justify-center"
                                )
                            }) {
                                Text((index + 1).toString())
                            }
                            TextArea(attrs = {
                                classes(
                                    "flex-1",
                                    "bg-white/5",
                                    "border",
                                    "border-white/10",
                                    "rounded-xl",
                                    "p-4",
                                    "text-white",
                                    "focus:border-nano-primary",
                                    "outline-none",
                                    "min-h-[80px]"
                                )
                                value(instruction.description)
                                onInput {
                                    instructions[index] = instruction.copy(description = it.value)
                                }
                            })
                            Button(attrs = {
                                classes(
                                    "p-3",
                                    "bg-red-500/10",
                                    "text-red-500",
                                    "rounded-xl",
                                    "hover:bg-red-500/20",
                                    "flex",
                                    "items-center"
                                )
                                onClick {
                                    instructions.removeAt(index)
                                    // Update steps
                                    val list = instructions.toList()
                                    instructions.clear()
                                    list.forEachIndexed { i, instr ->
                                        instructions.add(
                                            instr.copy(
                                                step = i + 1
                                            )
                                        )
                                    }
                                }
                            }) { MaterialIcon("delete") }
                        }
                    }
                    Button(attrs = {
                        classes(
                            "w-full",
                            "py-3",
                            "border-2",
                            "border-dashed",
                            "border-white/10",
                            "rounded-2xl",
                            "text-gray-400",
                            "hover:border-nano-primary",
                            "hover:text-nano-primary",
                            "transition-all",
                            "flex",
                            "items-center",
                            "justify-center",
                            "gap-2"
                        )
                        onClick { instructions.add(Instruction(instructions.size + 1, "")) }
                    }) {
                        MaterialIcon("add")
                        Text("Dodaj Korak")
                    }
                }
            }
        }
    }
}

@Composable
fun AdminSectionCard(title: String, content: @Composable () -> Unit) {
    Div(attrs = {
        classes(
            "bg-nano-surface",
            "p-8",
            "rounded-3xl",
            "border",
            "border-white/5",
            "shadow-xl"
        )
    }) {
        H3(attrs = {
            classes(
                "text-xl",
                "font-bold",
                "mb-6",
                "text-gray-300",
                "border-l-4",
                "border-nano-primary",
                "pl-4"
            )
        }) {
            Text(title)
        }
        content()
    }
}

@Composable
fun AdminFormField(label: String, content: @Composable () -> Unit) {
    Div(attrs = { classes("space-y-2", "mb-4") }) {
        Label(attrs = { classes("text-sm", "font-bold", "text-gray-500", "ml-1") }) { Text(label) }
        content()
    }
}

@Composable
fun EditCategoryModal(category: Category, onConfirm: (Category) -> Unit, onCancel: () -> Unit) {
    var categoryName by remember { mutableStateOf(category.name) }

    Div(attrs = {
        classes(
            "fixed",
            "inset-0",
            "z-[100]",
            "flex",
            "items-center",
            "justify-center",
            "p-4",
            "bg-black/90",
            "backdrop-blur-sm"
        )
        onClick { onCancel() }
    }) {
        Div(attrs = {
            classes(
                "bg-nano-surface",
                "w-full",
                "max-w-md",
                "rounded-3xl",
                "p-8",
                "shadow-2xl",
                "border",
                "border-white/10"
            )
            onClick { it.stopPropagation() }
        }) {
            H3(attrs = {
                classes(
                    "text-2xl",
                    "font-bold",
                    "mb-6",
                    "text-center"
                )
            }) { Text("Uredi Kategoriju") }

            Div(attrs = { classes("mb-8") }) {
                AdminFormField("Naziv Kategorije") {
                    Input(type = InputType.Text, attrs = {
                        classes(
                            "w-full",
                            "bg-white/5",
                            "border",
                            "border-white/10",
                            "rounded-xl",
                            "p-4",
                            "text-white",
                            "focus:border-nano-primary",
                            "outline-none"
                        )
                        value(categoryName)
                        onInput { categoryName = it.value }
                        placeholder("npr. Italijanska kuhinja")
                    })
                }
            }

            Div(attrs = { classes("flex", "gap-4") }) {
                Button(attrs = {
                    classes(
                        "flex-1",
                        "py-4",
                        "bg-white/5",
                        "hover:bg-white/10",
                        "text-white",
                        "font-bold",
                        "rounded-2xl",
                        "transition-all"
                    )
                    onClick { onCancel() }
                }) {
                    Text("Otkaži")
                }

                Button(attrs = {
                    classes(
                        "flex-1",
                        "py-4",
                        "bg-nano-primary",
                        "hover:bg-yellow-400",
                        "text-black",
                        "font-bold",
                        "rounded-2xl",
                        "shadow-lg",
                        "shadow-nano-primary/20",
                        "transition-all"
                    )
                    onClick { if (categoryName.isNotBlank()) onConfirm(category.copy(name = categoryName)) }
                }) {
                    Text("Sačuvaj")
                }
            }
        }
    }
}

@Composable
fun DeleteCategoryConfirmationModal(
    categoryName: String,
    onConfirm: () -> Unit,
    onCancel: () -> Unit
) {
    Div(attrs = {
        classes(
            "fixed",
            "inset-0",
            "z-50",
            "flex",
            "items-center",
            "justify-center",
            "p-4",
            "bg-black/80",
            "backdrop-blur-sm"
        )
        onClick { onCancel() }
    }) {
        Div(attrs = {
            classes(
                "bg-[#1A1AA1A]",
                "w-full",
                "max-w-md",
                "rounded-3xl",
                "p-8",
                "shadow-2xl",
                "border",
                "border-white/10"
            )
            onClick { it.stopPropagation() }
        }) {
            Div(attrs = {
                classes(
                    "w-20",
                    "h-20",
                    "bg-red-500/10",
                    "rounded-full",
                    "flex",
                    "items-center",
                    "justify-center",
                    "mx-auto",
                    "mb-6"
                )
            }) {
                MaterialIcon("delete_forever", "text-red-500 text-5xl")
            }

            H3(attrs = {
                classes(
                    "text-2xl",
                    "font-bold",
                    "mb-2",
                    "text-center"
                )
            }) { Text("Brisanje Kategorije") }

            P(attrs = { classes("text-gray-400", "mb-8", "leading-relaxed", "text-center") }) {
                Text("Da li ste sigurni da želite trajno obrisati kategoriju ")
                B(attrs = { classes("text-white") }) { Text(categoryName) }
                Text("?")
            }

            Div(attrs = { classes("flex", "gap-4") }) {
                Button(attrs = {
                    classes(
                        "flex-1",
                        "py-4",
                        "bg-white/5",
                        "hover:bg-white/10",
                        "text-white",
                        "font-bold",
                        "rounded-2xl",
                        "transition-all"
                    )
                    onClick { onCancel() }
                }) {
                    Text("Poništi")
                }

                Button(attrs = {
                    classes(
                        "flex-1",
                        "py-4",
                        "bg-red-500",
                        "hover:bg-red-600",
                        "text-white",
                        "font-bold",
                        "rounded-2xl",
                        "shadow-lg",
                        "shadow-red-500/20",
                        "transition-all"
                    )
                    onClick { onConfirm() }
                }) {
                    Text("Obriši")
                }
            }
        }
    }
}

@Composable
fun EditRecipePage(
    recipe: Recipe,
    categoriesState: ResultState<List<Category>>,
    onBack: () -> Unit,
    onSuccess: () -> Unit
) {
    var title by remember { mutableStateOf(recipe.title) }
    var description by remember { mutableStateOf(recipe.description) }
    var imageUrl by remember { mutableStateOf(recipe.imageUrl) }
    var category by remember { mutableStateOf(recipe.category) }
    var prepTime by remember { mutableStateOf(recipe.prepTime) }
    var level by remember { mutableStateOf(recipe.level) }
    var rating by remember { mutableStateOf(recipe.rating) }
    var promoted by remember { mutableStateOf(recipe.promoted ?: false) }
    var active by remember { mutableStateOf(recipe.active) }

    val ingredients =
        remember { mutableStateListOf<Ingredient>().apply { addAll(recipe.ingredients) } }
    val instructions =
        remember { mutableStateListOf<Instruction>().apply { addAll(recipe.instructions) } }

    var isSubmitting by remember { mutableStateOf(false) }
    var isUploading by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }

    fun handleFileUpload(file: org.w3c.files.File) {
        val reader = FileReader()
        reader.onload = { event ->
            val arrayBuffer = event.target.asDynamic().result as? org.khronos.webgl.ArrayBuffer
            if (arrayBuffer != null) {
                val bytes = org.khronos.webgl.Int8Array(arrayBuffer).unsafeCast<ByteArray>()
                isUploading = true
                RecipeService.uploadImage(bytes, file.name) { result ->
                    isUploading = false
                    if (result is ResultState.Success) {
                        imageUrl = result.data
                    } else if (result is ResultState.Error) {
                        errorMessage = result.message ?: "Greška pri uploadu slike"
                    }
                }
            }
        }
        reader.readAsArrayBuffer(file)
    }

    fun submit() {
        if (title.isBlank() || description.isBlank()) {
            errorMessage = "Naslov i opis su obavezni!"
            return
        }
        isSubmitting = true
        val updatedRecipe = recipe.copy(
            title = title,
            description = description,
            imageUrl = imageUrl,
            category = category,
            rating = rating,
            prepTime = prepTime,
            level = level,
            ingredients = ingredients.toList(),
            instructions = instructions.toList(),
            active = active,
            promoted = promoted
        )
        RecipeService.updateRecipe(updatedRecipe) { result ->
            isSubmitting = false
            if (result is ResultState.Success<*>) {
                onSuccess()
            } else if (result is ResultState.Error) {
                errorMessage = result.message ?: "Greška pri spremanju recepta"
            }
        }
    }

    DashboardHeader(
        title = "Uredi Recept",
        subtitle = "Ažurirajte informacije o receptu",
        onRefresh = { /* reset if needed */ },
        onAddRecipe = null,
        onBack = onBack,
        onSaveAction = { submit() },
        isSubmitting = isSubmitting,
        saveButtonText = "SAČUVAJ IZMJENE"
    )

    Main(attrs = {
        classes(
            "flex-1",
            "overflow-y-auto",
            "no-scrollbar",
            "p-8",
            "bg-nano-background/50"
        )
    }) {
        Div(attrs = { classes("max-w-4xl", "mx-auto", "space-y-8") }) {
            if (errorMessage != null) {
                Div(attrs = {
                    classes(
                        "bg-red-500/10",
                        "border",
                        "border-red-500/50",
                        "p-4",
                        "rounded-2xl",
                        "text-red-500",
                        "flex",
                        "justify-between",
                        "items-center"
                    )
                }) {
                    Text(errorMessage!!)
                    Button(attrs = { onClick { errorMessage = null } }) { MaterialIcon("close") }
                }
            }

            // Status Card
            AdminSectionCard("Status Recepta") {
                Div(attrs = { classes("flex", "gap-8") }) {
                    Div(attrs = { classes("flex", "items-center", "gap-3") }) {
                        Input(type = InputType.Checkbox, attrs = {
                            classes(
                                "w-6",
                                "h-6",
                                "rounded",
                                "accent-nano-primary",
                                "cursor-pointer"
                            )
                            checked(active)
                            onChange { active = it.value }
                        })
                        Label(attrs = {
                            classes(
                                "text-sm",
                                "font-bold",
                                "text-gray-300"
                            )
                        }) { Text("Aktivan (Vidi se u aplikaciji)") }
                    }
                    Div(attrs = { classes("flex", "items-center", "gap-3") }) {
                        Input(type = InputType.Checkbox, attrs = {
                            classes(
                                "w-6",
                                "h-6",
                                "rounded",
                                "accent-nano-primary",
                                "cursor-pointer"
                            )
                            checked(promoted)
                            onChange { promoted = it.value }
                        })
                        Label(attrs = {
                            classes(
                                "text-sm",
                                "font-bold",
                                "text-gray-300"
                            )
                        }) { Text("Istaknut Recept") }
                    }
                }
            }

            // Osnovne informacije
            AdminSectionCard("Osnovne Informacije") {
                AdminFormField("Naslov Recepta") {
                    Input(type = InputType.Text, attrs = {
                        classes(
                            "w-full",
                            "bg-white/5",
                            "border",
                            "border-white/10",
                            "rounded-xl",
                            "p-4",
                            "text-white",
                            "focus:border-nano-primary",
                            "outline-none"
                        )
                        value(title)
                        onInput { title = it.value }
                    })
                }

                AdminFormField("Opis") {
                    TextArea(attrs = {
                        classes(
                            "w-full",
                            "bg-white/5",
                            "border",
                            "border-white/10",
                            "rounded-xl",
                            "p-4",
                            "text-white",
                            "focus:border-nano-primary",
                            "outline-none",
                            "h-32"
                        )
                        value(description)
                        onInput { description = it.value }
                    })
                }

                Div(attrs = { classes("grid", "grid-cols-1", "sm:grid-cols-2", "gap-6") }) {
                    AdminFormField("Kategorija") {
                        Select(attrs = {
                            classes(
                                "w-full",
                                "bg-white/5",
                                "border",
                                "border-white/10",
                                "rounded-xl",
                                "h-[60px]",
                                "px-4",
                                "text-white",
                                "focus:border-nano-primary",
                                "outline-none",
                                "appearance-none",
                                "cursor-pointer",
                                "bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2020%2020%22%3E%3Cpath%20stroke%3D%22%239ca3af%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%221.5%22%20d%3D%22m6%208%204%204%204-4%22%2F%3E%3C%2Fsvg%3E')]",
                                "bg-[position:right_1.25rem_center]",
                                "bg-[length:1.25em_1.25em]",
                                "bg-no-repeat"
                            )
                            onInput { category = it.value ?: "" }
                        }) {
                            Option(
                                "",
                                attrs = { if (category == "") selected() }) { Text("Odaberi kategoriju") }
                            if (categoriesState is ResultState.Success) {
                                categoriesState.data.forEach { cat ->
                                    Option(value = cat.name, attrs = {
                                        if (cat.name == category) selected()
                                    }) {
                                        Text(cat.name)
                                    }
                                }
                            }
                        }
                    }
                    AdminFormField("Slika Recepta") {
                        Div(attrs = { classes("flex", "gap-4", "items-start") }) {
                            if (imageUrl.isNotBlank()) {
                                Img(
                                    src = imageUrl,
                                    attrs = {
                                        classes(
                                            "w-20",
                                            "h-20",
                                            "rounded-xl",
                                            "object-cover",
                                            "border",
                                            "border-white/10"
                                        )
                                    })
                            }
                            Div(attrs = { classes("flex-1", "relative") }) {
                                Input(type = InputType.File, attrs = {
                                    classes(
                                        "opacity-0",
                                        "absolute",
                                        "inset-0",
                                        "cursor-pointer",
                                        "z-10"
                                    )
                                    onChange {
                                        val file = it.target.files?.get(0)
                                        if (file != null) handleFileUpload(file)
                                    }
                                })
                                Div(attrs = {
                                    classes(
                                        "w-full",
                                        "bg-white/5",
                                        "border",
                                        "border-dashed",
                                        "border-white/20",
                                        "rounded-xl",
                                        "p-4",
                                        "text-center",
                                        "hover:border-nano-primary/50",
                                        "transition-all",
                                        "flex",
                                        "flex-col",
                                        "items-center",
                                        "gap-1"
                                    )
                                }) {
                                    if (isUploading) {
                                        Div(attrs = {
                                            classes(
                                                "w-5",
                                                "h-5",
                                                "border-2",
                                                "border-nano-primary",
                                                "border-t-transparent",
                                                "rounded-full",
                                                "animate-spin"
                                            )
                                        }) {}
                                        Text("Učitavanje...")
                                    } else {
                                        MaterialIcon("cloud_upload", "text-2xl text-gray-400")
                                        Text(if (imageUrl.isBlank()) "Izaberi sliku" else "Promijeni sliku")
                                    }
                                }
                            }
                        }
                    }
                }

                Div(attrs = {
                    classes(
                        "grid",
                        "grid-cols-1",
                        "sm:grid-cols-2",
                        "lg:grid-cols-3",
                        "gap-6"
                    )
                }) {
                    AdminFormField("Vrijeme pripreme") {
                        Input(type = InputType.Text, attrs = {
                            classes(
                                "w-full",
                                "bg-white/5",
                                "border",
                                "border-white/10",
                                "rounded-xl",
                                "h-[60px]",
                                "px-4",
                                "text-white",
                                "focus:border-nano-primary",
                                "outline-none"
                            )
                            value(prepTime)
                            onInput { prepTime = it.value }
                        })
                    }
                    AdminFormField("Nivo") {
                        Select(attrs = {
                            classes(
                                "w-full",
                                "bg-white/5",
                                "border",
                                "border-white/10",
                                "rounded-xl",
                                "h-[60px]",
                                "px-4",
                                "text-white",
                                "focus:border-nano-primary",
                                "outline-none",
                                "appearance-none",
                                "cursor-pointer",
                                "bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2020%2020%22%3E%3Cpath%20stroke%3D%22%239ca3af%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%221.5%22%20d%3D%22m6%208%204%204%204-4%22%2F%3E%3C%2Fsvg%3E')]",
                                "bg-[position:right_1.25rem_center]",
                                "bg-[length:1.25em_1.25em]",
                                "bg-no-repeat"
                            )
                            onInput { level = it.value ?: "Lako" }
                        }) {
                            listOf("Lako", "Srednje", "Teško").forEach {
                                Option(value = it, attrs = { if (it == level) selected() }) {
                                    Text(
                                        it
                                    )
                                }
                            }
                        }
                    }
                    AdminFormField("Rating (1-5)") {
                        Input(type = InputType.Number, attrs = {
                            classes(
                                "w-full",
                                "bg-white/5",
                                "border",
                                "border-white/10",
                                "rounded-xl",
                                "h-[60px]",
                                "px-4",
                                "text-white",
                                "focus:border-nano-primary",
                                "outline-none"
                            )
                            value(rating.toString())
                            onInput { rating = it.value?.toDouble() ?: 4.5 }
                        })
                    }
                }
            }

            // Sastojci
            AdminSectionCard("Sastojci") {
                Div(attrs = { classes("space-y-4") }) {
                    ingredients.forEachIndexed { index, ingredient ->
                        Div(attrs = {
                            classes(
                                "flex",
                                "flex-col",
                                "sm:flex-row",
                                "gap-4",
                                "items-start",
                                "sm:items-center"
                            )
                        }) {
                            Div(attrs = { classes("flex-1", "w-full") }) {
                                AdminFormField("Naziv") {
                                    Input(type = InputType.Text, attrs = {
                                        classes(
                                            "w-full",
                                            "bg-white/5",
                                            "border",
                                            "border-white/10",
                                            "rounded-xl",
                                            "p-3",
                                            "text-white"
                                        )
                                        value(ingredient.name)
                                        onInput {
                                            ingredients[index] = ingredient.copy(name = it.value)
                                        }
                                    })
                                }
                            }
                            Div(attrs = { classes("w-full", "sm:w-48") }) {
                                AdminFormField("Količina") {
                                    Input(type = InputType.Text, attrs = {
                                        classes(
                                            "w-full",
                                            "bg-white/5",
                                            "border",
                                            "border-white/10",
                                            "rounded-xl",
                                            "p-3",
                                            "text-white"
                                        )
                                        value(ingredient.amount)
                                        onInput {
                                            ingredients[index] = ingredient.copy(amount = it.value)
                                        }
                                    })
                                }
                            }
                            Button(attrs = {
                                classes(
                                    "p-3",
                                    "bg-red-500/10",
                                    "text-red-500",
                                    "rounded-xl",
                                    "hover:bg-red-500/20",
                                    "sm:mt-4",
                                    "flex",
                                    "items-center",
                                    "self-end",
                                    "sm:self-auto"
                                )
                                onClick { ingredients.removeAt(index) }
                            }) { MaterialIcon("delete") }
                        }
                    }
                    Button(attrs = {
                        classes(
                            "w-full",
                            "py-3",
                            "border-2",
                            "border-dashed",
                            "border-white/10",
                            "rounded-2xl",
                            "text-gray-400",
                            "hover:border-nano-primary",
                            "hover:text-nano-primary",
                            "transition-all",
                            "flex",
                            "items-center",
                            "justify-center",
                            "gap-2"
                        )
                        onClick { ingredients.add(Ingredient("", "")) }
                    }) {
                        MaterialIcon("add")
                        Text("Dodaj Sastojak")
                    }
                }
            }

            // Koraci pripreme
            AdminSectionCard("Koraci Pripreme") {
                Div(attrs = { classes("space-y-6") }) {
                    instructions.forEachIndexed { index, instruction ->
                        Div(attrs = { classes("flex", "gap-4") }) {
                            Div(attrs = {
                                classes(
                                    "w-10",
                                    "h-10",
                                    "bg-nano-primary",
                                    "text-black",
                                    "font-black",
                                    "rounded-xl",
                                    "flex-shrink-0",
                                    "flex",
                                    "items-center",
                                    "justify-center"
                                )
                            }) {
                                Text((index + 1).toString())
                            }
                            TextArea(attrs = {
                                classes(
                                    "flex-1",
                                    "bg-white/5",
                                    "border",
                                    "border-white/10",
                                    "rounded-xl",
                                    "p-4",
                                    "text-white",
                                    "min-h-[80px]"
                                )
                                value(instruction.description)
                                onInput {
                                    instructions[index] = instruction.copy(description = it.value)
                                }
                            })
                            Button(attrs = {
                                classes(
                                    "p-3",
                                    "bg-red-500/10",
                                    "text-red-500",
                                    "rounded-xl",
                                    "hover:bg-red-500/20",
                                    "flex",
                                    "items-center"
                                )
                                onClick {
                                    instructions.removeAt(index)
                                    val list = instructions.toList()
                                    instructions.clear()
                                    list.forEachIndexed { i, instr ->
                                        instructions.add(
                                            instr.copy(
                                                step = i + 1
                                            )
                                        )
                                    }
                                }
                            }) { MaterialIcon("delete") }
                        }
                    }
                    Button(attrs = {
                        classes(
                            "w-full",
                            "py-3",
                            "border-2",
                            "border-dashed",
                            "border-white/10",
                            "rounded-2xl",
                            "text-gray-400"
                        )
                        onClick { instructions.add(Instruction(instructions.size + 1, "")) }
                    }) {
                        MaterialIcon("add")
                        Text("Dodaj Korak")
                    }
                }
            }
        }
    }
}

@Composable
fun RecipeSkeletonGrid() {
    Div(attrs = {
        classes(
            "grid",
            "grid-cols-1",
            "sm:grid-cols-2",
            "md:grid-cols-3",
            "lg:grid-cols-4",
            "gap-6"
        )
    }) {
        repeat(8) {
            SkeletonLoader(listOf("h-48", "rounded-3xl"))
        }
    }
}
